function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _iterableToArrayLimit(arr, i) {
  var _i = null == arr ? null : "undefined" != typeof Symbol && arr[Symbol.iterator] || arr["@@iterator"];
  if (null != _i) {
    var _s,
      _e,
      _x,
      _r,
      _arr = [],
      _n = !0,
      _d = !1;
    try {
      if (_x = (_i = _i.call(arr)).next, 0 === i) {
        if (Object(_i) !== _i) return;
        _n = !1;
      } else for (; !(_n = (_s = _x.call(_i)).done) && (_arr.push(_s.value), _arr.length !== i); _n = !0);
    } catch (err) {
      _d = !0, _e = err;
    } finally {
      try {
        if (!_n && null != _i.return && (_r = _i.return(), Object(_r) !== _r)) return;
      } finally {
        if (_d) throw _e;
      }
    }
    return _arr;
  }
}
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from "prop-types";
import { tabbable } from 'tabbable';
import { keys } from '../../../../services';
import { EuiFocusTrap } from '../../../focus_trap';
import { EuiScreenReaderOnly } from '../../../accessibility';
import { EuiI18n } from '../../../i18n';

/**
 * This internal utility component is used by all cells, both header and body/footer cells.
 * It always handles:
 *   1. Removing any interactive children from keyboard tab order on cell mount
 *   2. Listening for focus on any interactive children and updating the cell focus context
 *
 * It should *only* render focus traps for:
 *   1. Header cells that are `actions: false` but still have interactive children
 *   2. Body cells that are `isExpandable: false` but still have interactive children
 */
export var HandleInteractiveChildren = function HandleInteractiveChildren(_ref) {
  var cellEl = _ref.cellEl,
    children = _ref.children,
    updateCellFocusContext = _ref.updateCellFocusContext,
    renderFocusTrap = _ref.renderFocusTrap;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    hasInteractiveChildren = _useState2[0],
    setHasInteractiveChildren = _useState2[1];

  // On mount, disable all interactive children
  useEffect(function () {
    if (cellEl) {
      var interactiveChildren = disableInteractives(cellEl);
      if (renderFocusTrap) {
        setHasInteractiveChildren(interactiveChildren.length > 0);
      }
    }
  }, [cellEl, renderFocusTrap]);

  // Ensure that any interactive children that are clicked update the latest cell focus context
  useEffect(function () {
    if (cellEl) {
      var onFocus = function onFocus() {
        return updateCellFocusContext();
      };
      cellEl.addEventListener('focus', onFocus, true); // useCapture listens for focus on children
      return function () {
        cellEl.removeEventListener('focus', onFocus, true);
      };
    }
  }, [cellEl, updateCellFocusContext]);
  var _children = useMemo(function () {
    return ___EmotionJSX(React.Fragment, null, children);
  }, [children]);
  if (!cellEl) return _children; // Do nothing if cell has yet to mount or is unmounting
  if (!renderFocusTrap) return _children; // Cells with default actions / expansion popovers do not need to trap
  if (!hasInteractiveChildren) return _children; // No need to focus trap if no children are interactive

  return ___EmotionJSX(FocusTrappedChildren, {
    cellEl: cellEl
  }, children);
};

/**
 * Cells with interactive children but no cell popover expansion should render a
 * focus trap that can be entered with the Enter key, which cycles keyboard tabs
 * through the cell contents only, and exited with the Escape key
 */
HandleInteractiveChildren.propTypes = {
  cellEl: PropTypes.oneOfType([PropTypes.any.isRequired, PropTypes.oneOf([null])]),
  updateCellFocusContext: PropTypes.func.isRequired,
  renderFocusTrap: PropTypes.bool
};
export var FocusTrappedChildren = ({
  cellEl,
  children
}) => {
  const [isCellEntered, setIsCellEntered] = useState(false);
  useEffect(() => {
    if (isCellEntered) {
      enableAndFocusInteractives(cellEl);
    } else {
      disableInteractives(cellEl);
    }
  }, [isCellEntered, cellEl]);
  useEffect(() => {
    const onKeyUp = event => {
      switch (event.key) {
        case keys.ENTER:
        case keys.F2:
          event.preventDefault();
          setIsCellEntered(true);
          break;
        case keys.ESCAPE:
          event.preventDefault();
          setIsCellEntered(isCellEntered => {
            if (isCellEntered === true) {
              requestAnimationFrame(() => cellEl.focus()); // move focus to cell
              return false;
            }
            return isCellEntered;
          });
          break;
      }
    };
    cellEl.addEventListener('keyup', onKeyUp);
    return () => {
      cellEl.removeEventListener('keyup', onKeyUp);
    };
  }, [cellEl]);
  return <EuiFocusTrap disabled={!isCellEntered} onDeactivation={() => setIsCellEntered(false)} clickOutsideDisables={true}>
      {children}

      <EuiScreenReaderOnly>
        <p>
          {' - '}
          <EuiI18n
        // eslint-disable-next-line local/i18n
        token="euiDataGridCell.focusTrapEnterPrompt" default="Press the Enter key to interact with this cell's contents." />
        </p>
      </EuiScreenReaderOnly>
    </EuiFocusTrap>;
};

/**
 * Utility fns for managing child interactive tabIndex state
 */
FocusTrappedChildren.propTypes = {
  cellEl: PropTypes.any.isRequired
};
const disableInteractives = cell => {
  const interactives = tabbable(cell);
  interactives.forEach(element => {
    element.setAttribute('data-euigrid-tab-managed', 'true');
    element.setAttribute('tabIndex', '-1');
  });
  return interactives;
};
const enableAndFocusInteractives = cell => {
  const interactives = cell.querySelectorAll('[data-euigrid-tab-managed]');
  interactives.forEach((element, i) => {
    element.setAttribute('tabIndex', '0');
    if (i === 0) {
      element.focus();
    }
  });
  return interactives;
};
try {
  HandleInteractiveChildren.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/datagrid/body/cell/focus_utils.tsx',
    description: 'This internal utility component is used by all cells, both header and body/footer cells.\n' + 'It always handles:\n' + '  1. Removing any interactive children from keyboard tab order on cell mount\n' + '  2. Listening for focus on any interactive children and updating the cell focus context\n' + '\n' + 'It should *only* render focus traps for:\n' + '  1. Header cells that are `actions: false` but still have interactive children\n' + '  2. Body cells that are `isExpandable: false` but still have interactive children',
    displayName: 'HandleInteractiveChildren',
    methods: [],
    props: {
      cellEl: {
        defaultValue: null,
        description: '',
        name: 'cellEl',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/datagrid/body/cell/focus_utils.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'HTMLElement'
        }
      },
      updateCellFocusContext: {
        defaultValue: null,
        description: '',
        name: 'updateCellFocusContext',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/datagrid/body/cell/focus_utils.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'Function'
        }
      },
      renderFocusTrap: {
        defaultValue: null,
        description: '',
        name: 'renderFocusTrap',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/datagrid/body/cell/focus_utils.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: []
  };
} catch (e) {}
try {
  FocusTrappedChildren.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/datagrid/body/cell/focus_utils.tsx',
    description: 'Cells with interactive children but no cell popover expansion should render a\n' + 'focus trap that can be entered with the Enter key, which cycles keyboard tabs\n' + 'through the cell contents only, and exited with the Escape key',
    displayName: 'FocusTrappedChildren',
    methods: [],
    props: {
      cellEl: {
        defaultValue: null,
        description: '',
        name: 'cellEl',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/datagrid/body/cell/focus_utils.tsx',
          name: 'TypeLiteral'
        }],
        required: true,
        type: {
          name: 'HTMLElement'
        }
      }
    },
    extendedInterfaces: []
  };
} catch (e) {}